import { useCallback, useState } from "react";
import { IPlayerSearchResponse, IPlayerZonePosition } from "../../../types/IPlayerSearchResponse";
import LabelText from "../../../ui/LabelText";
import SearchInput from "../../../ui/SearchInput";
import { debounceFunction } from "../../../utils/debounceFunction";
import IApiResponse from "../../../types/IApiResponse";
import { fetchData, postData } from "../../../services/api";
import apiUrls, { BASE_URL } from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import PlayerSearchList from "./PlayerSearchList";
import { useLoading } from "../../../contexts/LoadingContext";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";
import { IPlayerAttributesData } from "../../../types/IPlayerAttributesData";
import { fetchDataForSearchPlayer } from "../../../services/dataService";
import { SelectedPlayerAlteredSection, SelectedPlayerItem } from "../../../styled-components/PlayerComponent.styles";
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from "../../../contexts/ThemeContext";
import AlteredIcon from '../../../assets/icons/Altered.png'
import GreenAlteredIcon from '../../../assets/icons/GreenAltered.png'
import EyeIcon from '../../../assets/icons/EyeIcon.png'
import { useNavigate } from "react-router-dom";

interface SearchForSimilarPlayersProps {
    selectedPlayers: IPlayerSearchResponse[];
    onZonePositionData: (data: IPlayerZonePosition) => void;
    onPlayerAttributesData: (data: IPlayerAttributesData) => void;
    onRemovePlayer: (playerId: number) => void;
    onSelectPlayer: (playerId: number) => void;
}

const SearchForSimilarPlayers: React.FC<SearchForSimilarPlayersProps> = ({
    selectedPlayers, onZonePositionData, onPlayerAttributesData, onRemovePlayer, onSelectPlayer
}) => {
    const navigate = useNavigate();
    const { handleSelectedPlayerZones, handleSelectedProfiles, isAllPosition, handleSelectedAttributes, setIsSearchByAttributes,
        handleSelectedPlayer, isAltered, originPage
    } = usePlayerSearch();
    const { theme } = useTheme();
    const { startLoading, stopLoading } = useLoading();
    const [searchText, setSearchText] = useState<string>('');
    const [searchPlayerData, setSearchPlayerData] = useState<IPlayerSearchResponse[]>([]);
    const [clearInput, setClearInput] = useState(false);
    const [selectedItems, setSelectedItems] = useState<IPlayerSearchResponse[]>([]);

    const handleSearch = useCallback(
        debounceFunction(async (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            if (value.length >= 3) {
                setSearchText(value);
                startLoading();
                try {
                    const response: IApiResponse<IPlayerSearchResponse[]> = await fetchDataForSearchPlayer(value);
                    if (response.isSuccess) {
                        setSearchPlayerData(response.data); // Update the state with the data array directly
                    } else {
                        console.error(response.error?.message || 'Error fetching player data');
                    }
                } finally {
                    stopLoading();
                }
            }
            else {
                setSearchPlayerData?.([]);
            }
        }, 600), // Adjust the debounce delay (300ms in this case) as needed
        []
    );

    const handleClear = () => {
        setSearchText("");
        setSearchPlayerData?.([]);
    };

    const fetchPlayerZonePosition = async (playerId: number) => {
        try {
            startLoading();
            const response: IApiResponse<IPlayerZonePosition> = await fetchData(`${apiUrls['GetPlayerZonePositions']}?PlayerId=${playerId}`);
            handleApiResponse(response, (playerZonePositionData) => {
                onZonePositionData(playerZonePositionData);
            }, (error) => {
                console.error('Error fetching Player Zone Position Data:', error);
            });
        } catch (error) {
            console.error('Error fetching Player Zone Position Data:', error);
        } finally {
            stopLoading();
        }
    }

    const fetchAttributesData = async (playerId: number) => {
        try {
            startLoading();
            const ApiRequestData = {
                playerId: playerId,
                AllPosition: isAllPosition === 'allpositions',
                AttributeScoreFor: 'universe',
                seasonId: 0
            };
            const response: IApiResponse<IPlayerAttributesData> = await postData(
                apiUrls.GetPlayerAttributes, ApiRequestData
            );
            handleApiResponse(response, (attributesData) => {
                onPlayerAttributesData(attributesData);
            }, (error) => {
                console.error(error);

            });
        } catch (error) {
            console.error('Error fetching PlayerAttributes data:', error);
        } finally {
            stopLoading();
        }
    }

    const handleSelectPlayer = (item: IPlayerSearchResponse | IPlayerSearchResponse[] | null, selected: boolean) => {
        if (item !== null && Array.isArray(item)) {
            handleSelectedPlayer(item);
            fetchPlayerZonePosition(item[0].playerId);
            fetchAttributesData(item[0].playerId);
            handleClear();
            onSelectPlayer(item[0].playerId);
        }
    }

    const renderSearchPlayers = () => {
        return (
            searchPlayerData && searchPlayerData?.length > 0 && (
                <div className='search-player-list'>
                    <PlayerSearchList
                        searchData={searchPlayerData}
                        searchText={searchText}
                        onSelect={handleSelectPlayer}
                        selectedItems={selectedItems}
                        multiSelection={false} //isAdding
                        iconType='image'
                        displayCheckBox={false} />
                </div>
            ))
    }

    const removePlayer = (playerId: number) => {
        onRemovePlayer(playerId);
    }

    const handleViewProfile = () => {
        window.open(`${BASE_URL}/player/${selectedPlayers[0]?.playerId}`, '_blank');
    }

    const renderSelectedPlayers = useCallback(() => {
        return selectedPlayers.map((player) => (
            <SelectedPlayerItem className='selected-player-item'>
                <div className="selected-player-item-content">
                    <div className='player-text-data'>
                        <LabelText className='custom-list-strong-text'>{player.playerName}</LabelText>
                        <LabelText className={`player-team-text ${originPage !== 'Similarplayers Tab' && 'club-name'}`}>{player.playerClub}</LabelText>
                        {originPage !== 'Similarplayers Tab' && <div className='view-player' onClick={() => handleViewProfile()}>
                            <img src={EyeIcon} alt='view player' className='view-player-image' />
                            <LabelText className='view-player-text'>{"View Player"}</LabelText>
                        </div>
                        }
                    </div>
                    {originPage !== 'Similarplayers Tab' && <CloseIcon className='cta-btn-icons' onClick={() => removePlayer(player.playerId)} />}
                </div>
                {isAltered &&
                    <SelectedPlayerAlteredSection className="altered-section">
                        <img src={theme.mode === 'light' ? AlteredIcon : GreenAlteredIcon} className="altered-icon" alt="Altered Icon" />
                        <LabelText className='altered-text'>Altered</LabelText>
                    </SelectedPlayerAlteredSection>
                }
            </SelectedPlayerItem>
        ));
    }, [selectedPlayers, removePlayer, isAltered]);

    return (
        <>
            {(selectedPlayers.length) > 0 ? (
                renderSelectedPlayers()
            ) : <div className="player-search-content-only">
                <SearchInput
                    handleSearch={handleSearch}
                    handleClear={handleClear}
                    placeholder='Search player'
                    fontSize='20px'
                    type="SearchPlayer"
                    clearInput={clearInput}
                    autoFocus={false}
                />
                {renderSearchPlayers()}
            </div>}
        </>
    )
}

export default SearchForSimilarPlayers;